import { produce } from "immer";
import { AnyAction } from "redux";

import {
  AppointmentStatus,
  AppointmentStatusSlug,
} from "@/_constants/appointment.ts";
import { HistoryActionType } from "@/_constants/historyType.ts";
import { ServiceOrderType } from "@/_constants/serviceOrder.ts";
import { TenantType } from "@/_constants/tenantType.ts";
import { BusinessServiceType } from "@/common/enums/sharedEnums.ts";
import { ExternalUrl } from "@/common/interfaces";
import { Comment } from "@/common/type/sharedTypes";
import { IDwelling } from "@/modules/dwelling/reducers";
import { IUserMember } from "@/modules/myTeam/reducers";
import { IReferential } from "@/modules/referential/reducers";
import { GET_SERVICES_AGENDA_TEAM_SUCCESS } from "@/modules/serviceOrdering/constants";

export interface IERPRequest {
  adresse: string;
  code_postal: string;
  ville: string;
  vendeur_nom: string;
  acheteur_nom?: string;
}

export interface IERP {
  adresse?: string;
  code_postal?: string;
  ville?: string;
  vendeur_nom?: string;
  acheteur_nom?: string;
}

export interface IResponse {
  message?: string;
  error?: string;
}

export interface ITenant {
  id: string;
  person: IPerson;
  rentalStartDate: string;
  rentalEndDate: string;
  leaseStartDate: string;
  leaseEndDate: string;
  type: TenantType;
}

export interface IPerson {
  id: string;
  lastname: string;
  firstname: string;
  lastTenant: ITenant;
  email: string;
  phone: string;
}

export interface IKeysInfos {
  society: string;
  phone: string;
  email: string;
  address: string;
  further_information: string;
  contact_name: string;
  mobile: string;
  date: string;
  moment: boolean;
  at_partner: boolean;
}

export interface IAppointment {
  people: IPerson[];
  id: string;
  lastStatus: Status;
  startDate: string;
  endDate: string;
  appointmentStatuses?: any;
}

export interface IKey {
  lastStatus?: Status;
  startDate: string;
  endDate: string;
  appointmentStatuses?: any;
}
interface Status {
  id: string;
  status: {
    status: AppointmentStatus;
    action: string;
    order: number;
    slug: AppointmentStatusSlug;
  };
}

export interface History {
  action: HistoryActionType;
  createdAt: string;
  entityId: string;
  entityType: string;
  id: string;
  text: string;
}

export interface IService {
  datedReference?: string;
  isManagedByMainGroup: boolean;
  createdByUser?: IUserMember;
  orderedByUser?: IUserMember;
  id: string;
  title: string;
  dwelling: IDwelling;
  isArchived: boolean;
  comments: Comment[];
  user: IUserMember;
  appointment: IAppointment;
  diagnosticTypes?: DiagnosticType[];
  isInsuranceIssuedByAgency?: boolean;
  isAutomaticAssignation: boolean;
  type: ServiceOrderType;
  businessServiceType: BusinessServiceType;
  report?: {
    id: string;
    files: IFile[];
    serializedReport?: any;
  };
  histories: History[];
  mainFile: IFile;
  createdAt: string;
  permissions: string[];
  operators?: { label: string; value: string }[];
  externalUrls?: ExternalUrl[];
}

export interface IUpdateServiceOrderedBy {
  serviceId: string;
  userId: string;
}

export interface IUpdateServiceOperator {
  serviceId: string;
  operatorId: string;
}

export interface IUpdateServiceStatus {
  serviceId: string;
  slug: string;
}

export interface IUpdateServiceAppointment {
  serviceId: string;
  startDate: string;
  endDate: string;
}

export interface NotificationRecipient {
  id: string;
  tenantEmails?: string;
  tenantPhoneNumbers?: string;
  managerEmails?: string;
  operatorEmails?: string;
  operators?: { label: string; value: string }[];
  managers?: { label: string; value: string }[];
}

export interface DiagnosticType {
  name: string;
  slug: DiagnosticTypeValue;
  price: number;
  order: number;
}

export enum DiagnosticTypeValue {
  EDP = "EDP",
  EDPC = "EDPC",
  ASBESTOS = "ASBESTOS",
  LEAD = "LEAD",
  AREA = "AREA",
  ELECTRICITY = "ELECTRICITY",
  DERP = "DERP",
  GAS = "GAS",
  TERMITE = "TERMITE",
  CONDOMINIUM_VISIT = "CONDOMINIUM_VISIT",
}
export interface IFile {
  id?: string;
  path: string;
  type: IReferential;
  url?: string;
  createdAt?: string;
  serviceReportFile?: {
    locked: boolean;
  };
}

export interface IFileToUpload {
  content: any;
  type: object;
}

interface IState {
  servicesAgendaTeam: IService[];
}

const initState = {
  servicesAgendaTeam: [],
} as IState;

const serviceOrderingReducer = produce(
  (draft = initState, action: AnyAction) => {
    switch (action.type) {
      case GET_SERVICES_AGENDA_TEAM_SUCCESS:
        draft.servicesAgendaTeam = action.payload;
        break;
      default:
        return draft;
    }
  }
);

export default serviceOrderingReducer;
